import { DocumentModel } from '../text';
import { AnchorState, BlockLayout, FocusGeometry, TokenLayout } from './types';

export class LayoutIndex {
  private tokenLayouts: TokenLayout[] = [];
  private blockLayouts: BlockLayout[] = [];
  private totalContentHeight = 0;
  private epoch = 0;

  get totalHeight(): number {
    return this.totalContentHeight;
  }

  get currentEpoch(): number {
    return this.epoch;
  }

  /**
   * Calculates required pre-roll and post-roll padding in px
   * so first and last lines can align with the focus band center.
   */
  static calculateRollPadding(
    viewportHeight: number,
    focusCenterPercent: number
  ): { preRoll: number; postRoll: number } {
    const focusCenterY = viewportHeight * (focusCenterPercent / 100.0);
    const preRoll = Math.max(0, focusCenterY);
    const postRoll = Math.max(0, viewportHeight - focusCenterY);
    return { preRoll, postRoll };
  }

  /**
   * Measures DOM content container and tokens in UNTRANSFORMED layout space.
   */
  measure(
    container: HTMLElement,
    doc: DocumentModel
  ): void {
    this.epoch++;
    this.tokenLayouts = [];
    this.blockLayouts = [];

    const containerRect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;

    // Collect block elements
    const blockElements = container.querySelectorAll<HTMLElement>('[data-block-index]');

    let globalLineCount = 0;

    for (let b = 0; b < blockElements.length; b++) {
      const blockEl = blockElements[b]!;
      const blockIndex = parseInt(blockEl.getAttribute('data-block-index') ?? `${b}`, 10);
      const bRect = blockEl.getBoundingClientRect();

      const blockTop = bRect.top - containerRect.top + scrollTop;
      const blockHeight = bRect.height;

      // Measure tokens inside this block
      const textNode = blockEl.firstChild;
      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        const blockTokens = doc.tokens.filter((t) => t.block === blockIndex);
        const blockTextStart = doc.blocks[blockIndex]?.start ?? 0;

        let prevLineTop = -9999;
        let currentLineIndex = globalLineCount;

        for (const tok of blockTokens) {
          const relStart = Math.max(0, tok.start - blockTextStart);
          const relEnd = Math.min(textNode.textContent?.length ?? 0, tok.end - blockTextStart);

          if (relStart < relEnd) {
            try {
              const range = document.createRange();
              range.setStart(textNode, relStart);
              range.setEnd(textNode, relEnd);
              const rects = range.getClientRects();

              if (rects.length > 0) {
                const r = rects[0]!;
                const topRel = r.top - containerRect.top + scrollTop;

                // Detect line changes
                if (Math.abs(topRel - prevLineTop) > 4) {
                  prevLineTop = topRel;
                  currentLineIndex++;
                }

                this.tokenLayouts.push({
                  tokenIndex: tok.index,
                  blockIndex,
                  lineIndex: currentLineIndex,
                  lineTop: topRel,
                  lineHeight: r.height,
                  rect: {
                    left: r.left - containerRect.left,
                    top: topRel,
                    width: r.width,
                    height: r.height,
                  },
                });
              }
            } catch {
              // Ignore Range error if text node modified
            }
          }
        }
        globalLineCount = currentLineIndex + 1;
      }

      this.blockLayouts.push({
        blockIndex,
        top: blockTop,
        height: blockHeight,
        lineCount: globalLineCount,
      });
    }

    this.totalContentHeight = container.scrollHeight;
  }

  /**
   * Computes the scroll y needed to place the token's line center at focusCenter.
   */
  yForToken(
    tokenIndex: number,
    viewportHeight: number,
    focus: FocusGeometry
  ): number | null {
    const layout = this.tokenLayouts.find((t) => t.tokenIndex === tokenIndex);
    if (!layout) return null;

    const focusCenterY = viewportHeight * (focus.centerPercent / 100.0);
    const lineCenterY = layout.lineTop + layout.lineHeight / 2.0;

    return Math.max(0, lineCenterY - focusCenterY);
  }

  /**
   * Finds the token closest to the focus center line for a given scroll y.
   */
  tokenAtY(
    y: number,
    viewportHeight: number,
    focus: FocusGeometry
  ): number | null {
    if (this.tokenLayouts.length === 0) return null;

    const focusCenterY = y + viewportHeight * (focus.centerPercent / 100.0);

    let closestTokenIndex: number | null = null;
    let minDistance = Infinity;

    for (const t of this.tokenLayouts) {
      const lineCenter = t.lineTop + t.lineHeight / 2.0;
      const dist = Math.abs(lineCenter - focusCenterY);
      if (dist < minDistance) {
        minDistance = dist;
        closestTokenIndex = t.tokenIndex;
      }
    }

    return closestTokenIndex;
  }

  /**
   * Captures anchor for preservation before font/resize reflow
   */
  captureAnchor(
    y: number,
    viewportHeight: number,
    focus: FocusGeometry
  ): AnchorState | null {
    const tokenIndex = this.tokenAtY(y, viewportHeight, focus);
    if (tokenIndex === null) return null;

    const layout = this.tokenLayouts.find((t) => t.tokenIndex === tokenIndex);
    if (!layout) return null;

    const focusCenterY = y + viewportHeight * (focus.centerPercent / 100.0);
    const fractionalOffset = (focusCenterY - layout.lineTop) / (layout.lineHeight || 1.0);

    return {
      tokenIndex,
      fractionalLineOffset: fractionalOffset,
    };
  }

  /**
   * Restores scroll y after reflow so the anchored token lands at the same screen position
   */
  restoreAnchor(
    anchor: AnchorState,
    viewportHeight: number,
    focus: FocusGeometry
  ): number | null {
    const layout = this.tokenLayouts.find((t) => t.tokenIndex === anchor.tokenIndex);
    if (!layout) return null;

    const focusCenterY = viewportHeight * (focus.centerPercent / 100.0);
    const targetLineY = layout.lineTop + anchor.fractionalLineOffset * layout.lineHeight;

    return Math.max(0, targetLineY - focusCenterY);
  }
}
